import { ReactElement, useContext, useEffect, useState } from 'react';
import Image from 'next/image';
import clsx from 'clsx';

import { Button, Heading, Link } from '@appsumo/dorado-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

import { CartContext } from '~/contexts/cart';
import { useSavedCart } from '~/lib/cart/useCart';
import { getAvailableSavedDeals } from '~/lib/cart/util';
import { currencyWithCents } from '~/lib/format';
import { SavedDeal } from '~/types/cart';

import { Countdown } from '~/components/common/Countdown';

function SaveForLaterItem({
  deal,
  moveToCart,
}: {
  deal: SavedDeal;
  moveToCart: (dealId: number) => void;
}) {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const checkExpiration = () => {
      if (deal.dates?.end_date && new Date() > new Date(deal.dates.end_date)) {
        setExpired(true);
      } else {
        setExpired(false);
      }
    };

    const intervalId = setInterval(checkExpiration, 1e3); // every second (max of 5 saved deals/interval setups)
    return () => clearInterval(intervalId); // clears on unmount
  }, [deal.dates?.end_date]);

  if (expired) return <></>;

  const [planType] = deal.unique_plan_types;
  const pricingInfo = (
    <>
      <span className="text-sm font-bold text-midnight">
        {deal.is_free ? 'FREE' : currencyWithCents(deal.price)}
      </span>
      {!!deal.default_price && (
        <span className="ml-2 text-sm text-grace line-through">
          {currencyWithCents(deal.default_original_price)}
        </span>
      )}
    </>
  );

  return (
    <div className="relative mt-4 rounded bg-iceberg p-4">
      <div className="flex">
        <div className="flex">
          <div className="inline-block min-w-[48px]">
            <Link href={deal.get_absolute_url}>
              <Image
                src={`${deal.media_url}?width=48&height=32&aspect_ratio=3:2`}
                alt="deal-thumbnail"
                width={48}
                height={32}
                className="mt-1 rounded"
              />
            </Link>
          </div>
        </div>
        <div className="ml-2 flex flex-col">
          {!!deal.dates?.end_date && (
            <p className="text-sm font-bold text-brick">
              Hurry! Offer ends in&nbsp;
              <Countdown
                deadline={deal.dates.end_date}
                displayLabels={false}
                useWrapperFontSize
                inline
                noPadding
              />
            </p>
          )}
          <Link
            href={deal.get_absolute_url}
            className="w-fit font-medium"
            styled
          >
            {deal.public_name}
          </Link>
          <p className="text-sm text-black">{deal.subheader}</p>
          <p className="mt-1 text-sm text-grace">{planType}</p>
          <div className="lg:hidden">{pricingInfo}</div>
        </div>
        {/*
          min-w-165 below is roughly the width of 'Move to cart' button below...
          hard-coding min-w since position absolute & can't do something like min-w-fit.
          Same goes for min-h
        */}
        <div className="ml-auto hidden min-h-[68px] min-w-[165px] text-right lg:block">
          <div>{pricingInfo}</div>
          <div className="absolute bottom-4 right-4">
            <Button
              secondary
              className="whitespace-nowrap"
              onClick={() => {
                moveToCart(deal.id);
              }}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Move to cart
            </Button>
          </div>
        </div>
      </div>
      <div className="mt-4 lg:hidden">
        <Button
          secondary
          className="w-full whitespace-nowrap"
          onClick={() => {
            moveToCart(deal.id);
          }}
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add to cart
        </Button>
      </div>
    </div>
  );
}

export default function SavedForLater({
  className,
}: {
  className?: string;
}): ReactElement {
  const { savedItems: items } = useContext(CartContext);
  const { moveToCart } = useSavedCart();
  const [savedDeals, setSavedDeals] = useState<SavedDeal[]>([]);

  useEffect(
    // useEffect here to prevent client hydration issues (celery task clears out expired deals)
    () => {
      const availSavedDeals = getAvailableSavedDeals(items);
      setSavedDeals(availSavedDeals);
    },
    [items],
  );

  if (!items || !savedDeals?.length) return <></>;

  // todo: do we want to put loading/spinner states on move to cart / remove from saved?
  // todo: idea - only show countdown if <= 2 days left?

  return (
    <div
      className={clsx('lg:col-start-1 lg:col-end-1', className)}
      data-testid="saved-for-later-container"
    >
      <div className="flex items-center">
        <Heading.H3 className="font-semibold">Saved for later</Heading.H3>
        {!!items?.collection_url && (
          <Link
            href={items.collection_url}
            className="ml-auto"
            styled
            data-testid="saved-for-later-view-all"
          >
            View all ({savedDeals.length})
          </Link>
        )}
      </div>
      {savedDeals.map((deal: SavedDeal) => (
        <SaveForLaterItem key={deal.id} deal={deal} moveToCart={moveToCart} />
      ))}
    </div>
  );
}

export { SavedForLater };
